
import { permissionManager } from "@/utils/permissions/PermissionManager";
import { useEffect, useState } from "react";

export const useHasPermission = (permission: string): boolean => {
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkPermission = () => {
      setHasAccess(permissionManager.hasPermission(permission));
    };

    // Initial check
    checkPermission();

    // Subscribe to permission changes
    const unsubscribe = permissionManager.subscribe(() => {
      checkPermission();
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [permission]);

  return hasAccess;
};

// Create a usePermissions hook that provides multiple permission-related utilities
export const usePermissions = () => {
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  
  useEffect(() => {
    // Get initial permissions
    setPermissions(permissionManager.getPermissions());
    
    // Subscribe to permission changes
    const unsubscribe = permissionManager.subscribe((state) => {
      setPermissions(state.permissions);
    });
    
    return unsubscribe;
  }, []);
  
  return {
    hasPermission: (permission: string) => permissionManager.hasPermission(permission),
    hasAnyPermission: (permissions: string[]) => permissionManager.hasAnyPermission(permissions),
    hasAllPermissions: (permissions: string[]) => permissionManager.hasAllPermissions(permissions),
    hasRole: (role: any) => permissionManager.hasRole(role),
    isAdmin: () => permissionManager.isAdmin(),
    permissions,
    syncState: permissionManager.getSyncState(),
  };
};

export const expandedPermissions = {
  "view:blog": ["admin", "editor"],
  "manage:blog": ["admin", "editor"],
  "view:users": ["admin"],
  "manage:users": ["admin"],
  "view:testimonials": ["admin", "moderator"],
  "manage:testimonials": ["admin", "moderator"],
  "view:settings": ["admin"],
  "manage:settings": ["admin"],
  "view:dashboard": ["admin", "user", "moderator", "editor"],
  "view:profile": ["admin", "user", "moderator", "editor"],
  "manage:newsletters": ["admin", "editor"],
  "view:newsletters": ["admin", "editor", "moderator"],
  "view:linkedin": ["admin", "editor"],       // Added LinkedIn permissions
  "manage:linkedin": ["admin", "editor"]      // Added LinkedIn permissions
};
